import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';

const Login = () => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const isRegister = mode === 'register';

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = isRegister
        ? await register(
            form.name,
            form.email,
            form.password,
            form.password_confirmation
          )
        : await login(form.email, form.password);

      if (!response.success) {
        setError(response.message || 'Something went wrong, please try again.');
      }
    } catch (err) {
      setError('Unexpected error, please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-layout">
      <section className="auth-hero">
        <div className="auth-hero-content">
          <div>
            <div className="badge badge-muted" style={{ color: 'white' }}>
              Release 1.0 · Control Center
            </div>
            <h1 style={{ fontSize: '3rem', lineHeight: 1.1, margin: '1.5rem 0' }}>
              A modern operating system for your entire platform
            </h1>
            <p style={{ fontSize: '1.05rem', color: 'rgba(255,255,255,0.85)' }}>
              Monitor activation, orchestrate launches, and empower your team with
              out-of-the-box authentication and user management.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
            <div>
              <p style={{ color: 'rgba(255,255,255,0.65)', marginBottom: '0.35rem' }}>
                Active installs
              </p>
              <h3 style={{ fontSize: '2.5rem', margin: 0 }}>48</h3>
            </div>
            <div>
              <p style={{ color: 'rgba(255,255,255,0.65)', marginBottom: '0.35rem' }}>
                Avg. response
              </p>
              <h3 style={{ fontSize: '2.5rem', margin: 0 }}>1h 14m</h3>
            </div>
            <div>
              <p style={{ color: 'rgba(255,255,255,0.65)', marginBottom: '0.35rem' }}>
                Uptime
              </p>
              <h3 style={{ fontSize: '2.5rem', margin: 0 }}>99.98%</h3>
            </div>
          </div>
        </div>
      </section>

      <section className="auth-panel">
        <div className="auth-card">
          <div className="auth-toggle">
            <button
              type="button"
              className={isRegister ? '' : 'active'}
              onClick={() => setMode('login')}
            >
              Sign in
            </button>
            <button
              type="button"
              className={isRegister ? 'active' : ''}
              onClick={() => setMode('register')}
            >
              Create account
            </button>
          </div>

          <header style={{ marginBottom: '1.4rem' }}>
            <h2 style={{ fontSize: '1.8rem', marginBottom: '0.3rem' }}>
              {isRegister ? 'Welcome aboard 🚀' : 'Welcome back 👋'}
            </h2>
            <p className="text-muted">
              {isRegister
                ? 'Create a workspace to start shipping faster.'
                : 'Enter your credentials to continue.'}
            </p>
          </header>

          {error && <div className="error-banner">{error}</div>}

          <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '1rem' }}>
            {isRegister && (
              <div className="field">
                <label htmlFor="name">Full name</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  className="input"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Ava Thompson"
                  required
                />
              </div>
            )}

            <div className="field">
              <label htmlFor="email">Work email</label>
              <input
                id="email"
                name="email"
                type="email"
                className="input"
                value={form.email}
                onChange={handleChange}
                placeholder="you@company.com"
                required
              />
            </div>

            <div className="field">
              <label htmlFor="password">Password</label>
              <div className="input-with-action">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  className="input"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  minLength={8}
                  required
                />
                <button
                  type="button"
                  className="input-action"
                  aria-pressed={showPassword}
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              {!isRegister && (
                <div style={{ marginTop: '0.4rem', textAlign: 'right' }}>
                  <Link to="/forgot-password" className="text-muted" style={{ fontWeight: 600 }}>
                    Forgot password?
                  </Link>
                </div>
              )}
            </div>

            {isRegister && (
              <div className="field">
                <label htmlFor="password_confirmation">Confirm password</label>
                <div className="input-with-action">
                  <input
                    id="password_confirmation"
                    name="password_confirmation"
                    type={showConfirmPassword ? 'text' : 'password'}
                    className="input"
                    value={form.password_confirmation}
                    onChange={handleChange}
                    placeholder="Repeat password"
                    minLength={8}
                    required
                  />
                  <button
                    type="button"
                    className="input-action"
                    aria-pressed={showConfirmPassword}
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                  >
                    {showConfirmPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>
            )}

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading
                ? 'Please wait...'
                : isRegister
                ? 'Create your workspace'
                : 'Access dashboard'}
            </button>
          </form>

          <p className="text-muted" style={{ marginTop: '1.5rem', fontSize: '0.85rem' }}>
            By continuing you agree to our{' '}
            <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Privacy Policy</span> &{' '}
            <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Terms</span>.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Login;
